<template>
  <div>
    <div
      class="ghl-form-survey-text-style-editor flex items-center gap-1.5 px-2 py-1.5"
      :class="isSubmitMessage ? 'flex-wrap whitespace-normal' : 'flex-wrap'"
      :style="{ borderTop: '1px solid #E5E7EB' }"
    >
      <RTEControlFontFamily
        :editor="editor"
        :fallback-font="fallbackFont"
        :font-family-options="FONT_FAMILY_OPTIONS"
      />
      <RTEControlTextBlocks :editor="editor" />
      <RTEControlFontSize
        :editor="editor"
        :fallback-font-size="fallbackFontSize"
      />
      <div
        v-if="!isSubmitMessage && size !== 'mid'"
        class="h-0 basis-full"
      ></div>
      <RTEControlBold :editor="editor" />
      <RTEControlItalic :editor="editor" />
      <RTEControlUnderline :editor="editor" />
      <RTEControlStrike :editor="editor" tooltip-label="Strikethrough" />

      <div v-if="!mobileInline" class="overflow-menu-icon">
        <RTEControlColorPicker
          :editor="editor"
          :default-color="defaultColor"
          tooltip-label="Text color"
        />
      </div>
      <div class="overflow-menu-icon">
        <RTEControlColorPicker
          :editor="editor"
          color-for="highlight"
          tooltip-label="Background color"
        />
      </div>

      <UIDivider :vertical="true" />
      <div
        v-if="!isSubmitMessage && size === 'mid'"
        class="h-0 basis-full"
      ></div>

      <UIPopover trigger="click">
        <template #trigger>
          <div>
            <UITooltip>
              <template #trigger>
                <AlignLeftIcon class="h-5 w-5 py-0.5 text-gray-600" />
              </template>
              {{ t("textEditor.textAlign") }}
            </UITooltip>
          </div>
        </template>
        <div class="flex gap-2" @mousedown.capture="onPopoverToolbarMouseDown">
          <RTEControlTextAlignment :editor="editor" align="left" />
          <RTEControlTextAlignment :editor="editor" align="center" />
          <RTEControlTextAlignment :editor="editor" align="right" />
          <RTEControlTextAlignment :editor="editor" align="justify" />
        </div>
      </UIPopover>

      <UIDivider v-if="!mobileInline" :vertical="true" />

      <UIPopover trigger="click">
        <template #trigger>
          <div>
            <UITooltip>
              <template #trigger>
                <ListIcon class="h-5 w-5 py-0.5 text-gray-600" />
              </template>
              {{ t("textEditor.list") }}
            </UITooltip>
          </div>
        </template>
        <div class="flex gap-2" @mousedown.capture="onPopoverToolbarMouseDown">
          <RTEControlList :editor="editor" list-type="bulletList" />
          <RTEControlList :editor="editor" list-type="orderedList" />
        </div>
      </UIPopover>

      <UIDivider v-if="!mobileInline" :vertical="true" />
      <div
        v-if="!isSubmitMessage && size !== 'mid'"
        class="h-0 basis-full"
      ></div>

      <RTEControlSourceCode :editor="editor" />
      <RTEControlCode :editor="editor" tooltip-label="Inline code block" />
      <RTEControlCode
        :editor="editor"
        type="block"
        tooltip-label="Code block"
      />

      <UIDivider v-if="!mobileInline" :vertical="true" />

      <OverflowMenu :editor="editor" />
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-ignore - icon types available at runtime
import { AlignLeftIcon, ListIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  RTEControlBold,
  RTEControlCode,
  RTEControlColorPicker,
  RTEControlFontFamily,
  RTEControlFontSize,
  RTEControlItalic,
  RTEControlList,
  RTEControlStrike,
  RTEControlTextAlignment,
  RTEControlTextBlocks,
  RTEControlUnderline,
} from "@gohighlevel/ghl-text-editor";
import { UIDivider, UIPopover, UITooltip } from "@gohighlevel/ghl-ui";
// @ts-ignore - editor types available at runtime
import { Editor } from "@tiptap/vue-3";
import { inject } from "vue";
import { useI18n } from "vue-i18n";
import OverflowMenu from "./OverflowMenu.vue";
import RTEControlSourceCode from "./RTEControlSourceCode.vue";
import { FONT_FAMILY_OPTIONS } from "./constants";
import { formSurveyRtePrepareSelectAllKey } from "./formSurveyRteToolbar";

const { t } = useI18n();

defineProps<{
  editor: Editor;
  size?: string;
  mobileInline?: boolean;
  isSubmitMessage?: boolean;
  fallbackFont?: string;
  fallbackFontSize?: number;
  defaultColor?: string;
}>();

const prepareSelectAllForToolbar = inject(
  formSurveyRtePrepareSelectAllKey,
  undefined
);

/**
 * @description Capture on alignment/list popover panels (may teleport outside the
 * toolbar wrapper). Runs the same full-document selection as `index.vue` before
 * the control handles the click. No-op when `FooterStyleMenu` is not under the provider.
 * @returns {void}
 */
const onPopoverToolbarMouseDown = (): void => {
  prepareSelectAllForToolbar?.();
};
</script>

<style scoped>
.ghl-form-survey-text-style-editor {
  user-select: none;
  -webkit-user-select: none;
}

.ghl-form-survey-text-style-editor .ghl-color-picker__container,
.ghl-form-survey-text-style-editor .n-color-picker.compact-picker {
  height: 24px;
  width: 24px;
  --n-height: 24px;
}

.ghl-form-survey-text-style-editor
  #toolbar_select--font_family
  :deep(.n-base-selection),
.font-family {
  width: 100%;
  max-width: 138px;
  min-width: 95px;
}

.ghl-form-survey-text-style-editor
  #toolbar_select--font_size
  :deep(.n-base-selection),
.font-size {
  width: 75px;
  min-width: 75px;
}
.ghl-form-survey-text-style-editor #toolbar_select--text_block {
  width: 110px;
}
.ghl-form-survey-text-style-editor #toolbar_select--line_height {
  min-width: 73px;
}

.ghl-form-survey-text-style-editor .n-base-selection {
  --n-height: 24px !important;
  --n-border-radius: 5px !important;
}
.ghl-form-survey-text-style-editor .n-base-selection .n-base-selection__border,
.ghl-form-survey-text-style-editor
  .n-base-selection
  .n-base-selection__state-border {
  border-radius: var(--n-border-radius);
}
.ghl-form-survey-text-style-editor
  .n-base-selection
  .n-base-selection-label
  .n-base-selection-input
  .n-base-selection-input__content {
  font-size: 12px;
}
.n-base-select-option__content {
  font-size: 12px;
}

.overflow-menu-icon {
  border-radius: 0.25rem;
  outline: none;
  transition: 0.3s ease-in-out;
}
.overflow-menu-icon:hover {
  background-color: #e8f3fe;
}

.ghl-form-survey-text-style-editor .ghl-color-picker__container {
  position: relative;
}
.ghl-form-survey-text-style-editor
  .ghl-color-picker__container
  .hu-color-picker {
  top: unset;
  bottom: 2rem;
}

.ghl-form-survey-text-style-editor .toolbar_button img {
  height: 16px;
  width: 16px;
}

.ghl-form-survey-text-style-editor
  :deep(.n-base-selection .n-base-selection-label) {
  background-color: transparent !important;
}
</style>
